import {
  AUTO_COLLECT_DURATION_MS,
  FIRST_SESSION_DELAY_MS,
  REWARDED_GLOBAL_COOLDOWN_MS,
  discountedIntegerCost,
} from '../config/rewardedAds';
import { catalogItem, purchaseCost } from '../config/catalog';
import { starterGateActive } from '../sim/starterGate';
import { starterPlotIds } from '../spatial/plots';
import type { EntityKind, ParkState } from '../types';
import { isRewardedAdsEnabled } from './adsEnabled';
import {
  emitGameplayStart,
  emitGameplayStop,
  initCrazyGamesSdk,
  requestCrazyGamesRewardedAd,
  type CrazyGamesAdCallbacks,
} from './crazyGamesSdk';
import {
  getRewardedState,
  loadRewardedState,
  updateRewardedState,
  type RewardedPersistState,
} from './rewardedState';

export type RewardedKind = 'build_discount' | 'land_discount' | 'auto_collect' | 'offline_double';

export type RewardedRequestResult =
  | { ok: true; kind: RewardedKind }
  | { ok: false; reason: 'busy' | 'disabled' | 'ineligible' | 'cooldown' | 'ad_error' | 'unavailable' };

type EconomyPauseHook = (paused: boolean) => void;
type OverlayHook = (active: boolean) => void;
type MuteHook = (muted: boolean) => void;
type MessageHook = (key: 'rewarded.unavailable') => void;

let busy = false;
let economyPaused = false;
let hooks: {
  setEconomyPaused?: EconomyPauseHook;
  setOverlay?: OverlayHook;
  setPlatformMuted?: MuteHook;
  onUnavailable?: MessageHook;
} = {};

loadRewardedState();

export function setRewardedAdHooks(next: typeof hooks): void {
  hooks = next;
}

export function resetRewardedRuntimeForTests(): void {
  busy = false;
  economyPaused = false;
  hooks = {};
}

export function isRewardedBusy(): boolean {
  return busy;
}

export function isEconomyPaused(): boolean {
  return economyPaused;
}

export function setEconomyPaused(paused: boolean): void {
  economyPaused = paused;
  hooks.setEconomyPaused?.(paused);
}

export function isEstablishedPark(state: ParkState): boolean {
  const milestones = new Set(state.builtKindMilestones ?? []);
  if (milestones.size >= 2) {
    return true;
  }
  const starter = state.entities.find((entity) => entity.kind === 'starter_ride');
  if (starter && starter.level >= 2) {
    return true;
  }
  const starters = new Set(starterPlotIds());
  return state.unlockedLandIds.some((id) => !starters.has(id));
}

export function isRewardedEligible(
  state: ParkState,
  now: number,
  sessionStartedAt: number,
  enabled = isRewardedAdsEnabled(),
): boolean {
  if (!enabled) {
    return false;
  }
  if (starterGateActive(state)) {
    return false;
  }
  if (!isEstablishedPark(state) && now - sessionStartedAt < FIRST_SESSION_DELAY_MS) {
    return false;
  }
  return true;
}

export function canShowBoostChrome(args: {
  eligible: boolean;
  worldInteractionActive: boolean;
  pinchActive: boolean;
  placementActive: boolean;
  floorMode: boolean;
}): boolean {
  return (
    args.eligible &&
    !args.worldInteractionActive &&
    !args.pinchActive &&
    !args.placementActive &&
    !args.floorMode
  );
}

export function isRewardedSurfaceIdle(args: {
  buildMode: boolean;
  landMode: boolean;
  floorMode: boolean;
  placementActive: boolean;
  worldInteractionActive: boolean;
  pinchActive: boolean;
  boostsOpen: boolean;
  inspectingEntity: boolean;
  inspectingLand: boolean;
}): boolean {
  return (
    !args.buildMode &&
    !args.landMode &&
    !args.floorMode &&
    !args.placementActive &&
    !args.worldInteractionActive &&
    !args.pinchActive &&
    !args.boostsOpen &&
    !args.inspectingEntity &&
    !args.inspectingLand
  );
}

export function shouldSurfacePendingOfflineOffer(args: {
  pending: boolean;
  offerClosed: boolean;
  alreadyOpen: boolean;
  eligible: boolean;
  idle: boolean;
}): boolean {
  return args.pending && !args.offerClosed && !args.alreadyOpen && args.eligible && args.idle;
}

export function rewardedCooldownRemainingMs(now: number, state: RewardedPersistState = getRewardedState()): number {
  if (state.lastRewardedAt == null) {
    return 0;
  }
  return Math.max(0, state.lastRewardedAt + REWARDED_GLOBAL_COOLDOWN_MS - now);
}

export function hasAttractionBuildCoupon(state: RewardedPersistState = getRewardedState()): boolean {
  return state.nextAttractionDiscount === true;
}

export function landDiscountParcelId(state: RewardedPersistState = getRewardedState()): string | null {
  return state.landDiscountParcelId;
}

export function isAutoCollectActive(now: number, state: RewardedPersistState = getRewardedState()): boolean {
  return state.autoCollectUntil != null && state.autoCollectUntil > now;
}

export function autoCollectRemainingMs(now: number, state: RewardedPersistState = getRewardedState()): number {
  if (state.autoCollectUntil == null) {
    return 0;
  }
  return Math.max(0, state.autoCollectUntil - now);
}

export function attractionEffectiveCost(kind: EntityKind, coupon = hasAttractionBuildCoupon()): number {
  const base = purchaseCost(kind);
  if (catalogItem(kind).role === 'attraction' && coupon) {
    return discountedIntegerCost(base);
  }
  return base;
}

export function landEffectiveCost(baseCost: number, parcelId: string): number {
  return landDiscountParcelId() === parcelId ? discountedIntegerCost(baseCost) : baseCost;
}

export function consumeAttractionBuildCoupon(): boolean {
  if (!hasAttractionBuildCoupon()) {
    return false;
  }
  updateRewardedState({ nextAttractionDiscount: false });
  return true;
}

export function consumeLandDiscount(parcelId: string): boolean {
  if (landDiscountParcelId() !== parcelId) {
    return false;
  }
  updateRewardedState({ landDiscountParcelId: null });
  return true;
}

export function applyRewardedGrant(
  kind: RewardedKind,
  now: number,
  extras?: { parcelId?: string; grantOfflineExtra?: () => void },
): void {
  if (kind === 'build_discount') {
    updateRewardedState({
      nextAttractionDiscount: true,
      lastRewardedAt: now,
    });
    return;
  }
  if (kind === 'land_discount') {
    updateRewardedState({
      landDiscountParcelId: extras?.parcelId ?? null,
      lastRewardedAt: now,
    });
    return;
  }
  if (kind === 'auto_collect') {
    updateRewardedState({
      autoCollectUntil: now + AUTO_COLLECT_DURATION_MS,
      lastRewardedAt: now,
    });
    return;
  }
  extras?.grantOfflineExtra?.();
  updateRewardedState({ lastRewardedAt: now });
}

function restoreAfterBreak(): void {
  hooks.setPlatformMuted?.(false);
  setEconomyPaused(false);
  hooks.setOverlay?.(false);
  busy = false;
  emitGameplayStart();
}

export async function requestRewardedAd(
  kind: RewardedKind,
  args: {
    now: number;
    state: ParkState;
    sessionStartedAt: number;
    parcelId?: string;
    grantOfflineExtra?: () => void;
    enabled?: boolean;
    clock?: () => number;
  },
): Promise<RewardedRequestResult> {
  const enabled = args.enabled ?? isRewardedAdsEnabled();
  if (!enabled) {
    return { ok: false, reason: 'disabled' };
  }
  if (busy) {
    return { ok: false, reason: 'busy' };
  }
  if (!isRewardedEligible(args.state, args.now, args.sessionStartedAt, enabled)) {
    return { ok: false, reason: 'ineligible' };
  }
  if (rewardedCooldownRemainingMs(args.now) > 0) {
    return { ok: false, reason: 'cooldown' };
  }

  busy = true;
  setEconomyPaused(true);
  hooks.setOverlay?.(true);
  await initCrazyGamesSdk();
  emitGameplayStop();

  return new Promise((resolve) => {
    let settled = false;
    const finish = (result: RewardedRequestResult) => {
      if (settled) {
        return;
      }
      settled = true;
      resolve(result);
    };
    const callbacks: CrazyGamesAdCallbacks = {
      adStarted: () => {
        hooks.setPlatformMuted?.(true);
      },
      adFinished: () => {
        const completionNow = args.clock?.() ?? Date.now();
        applyRewardedGrant(kind, completionNow, {
          parcelId: args.parcelId,
          grantOfflineExtra: args.grantOfflineExtra,
        });
        restoreAfterBreak();
        finish({ ok: true, kind });
      },
      adError: () => {
        restoreAfterBreak();
        hooks.onUnavailable?.('rewarded.unavailable');
        finish({ ok: false, reason: 'ad_error' });
      },
    };
    try {
      requestCrazyGamesRewardedAd(callbacks);
    } catch {
      callbacks.adError({ code: 'unavailable' });
    }
  });
}

export { discountedIntegerCost };
